// src/App.js

import {
    BrowserRouter,
    Routes,
    Route,
    Navigate
} from "react-router-dom";

import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import AdminDashboard from "./pages/AdminDashboard";
import FacilityDetails from "./pages/FacilityDetails";
import LayoutEditor from "./pages/LayoutEditor";

import { useAuth } from "./AuthContext";

// ============================================
// FULLSCREEN LOADER
// ============================================

function FullscreenLoader({
    text = "Loading application..."
}) {

    return (

        <div className="
            h-screen
            flex
            items-center
            justify-center
            bg-gray-100
        ">

            <div className="
                flex
                flex-col
                items-center
                gap-4
            ">

                <div className="
                    w-14
                    h-14
                    border-4
                    border-blue-500
                    border-t-transparent
                    rounded-full
                    animate-spin
                " />

                <p className="
                    text-gray-600
                    font-medium
                ">
                    {text}
                </p>

            </div>

        </div>
    );
}

// ============================================
// PROTECTED ROUTE
// ============================================

function ProtectedRoute({ children }) {

    const {
        token,
        loading
    } = useAuth();

    // ========================================
    // AUTH LOADING
    // ========================================

    if (loading) {

        return (
            <FullscreenLoader />
        );
    }

    // ========================================
    // NO TOKEN
    // ========================================

    if (!token) {

        return (
            <Navigate
                to="/login"
                replace
            />
        );
    }

    // ========================================
    // AUTHORIZED
    // ========================================

    return children;
}

// ============================================
// PUBLIC ROUTE
// PREVENT ACCESS AFTER LOGIN
// ============================================

function PublicRoute({ children }) {

    const {
        token,
        loading
    } = useAuth();

    // ========================================
    // WAIT FOR AUTH INIT
    // ========================================

    if (loading) {

        return (
            <FullscreenLoader />
        );
    }

    // ========================================
    // ALREADY LOGGED IN
    // ========================================

    if (token) {

        return (
            <Navigate
                to="/dashboard"
                replace
            />
        );
    }

    return children;
}

// ============================================
// MAIN APP
// ============================================

function App() {

    return (

        <BrowserRouter>

            <Routes>

                {/* ================================= */}
                {/* LOGIN */}
                {/* ================================= */}

                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <Login />
                        </PublicRoute>
                    }
                />

                {/* ================================= */}
                {/* SIGNUP */}
                {/* ================================= */}

                <Route
                    path="/signup"
                    element={
                        <PublicRoute>
                            <SignUp />
                        </PublicRoute>
                    }
                />

                {/* ================================= */}
                {/* DASHBOARD */}
                {/* ================================= */}

                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <AdminDashboard />
                        </ProtectedRoute>
                    }
                />

                {/* ================================= */}
                {/* FACILITY DETAILS */}
                {/* ================================= */}

                <Route
                    path="/facility/:facilityId"
                    element={
                        <ProtectedRoute>
                            <FacilityDetails />
                        </ProtectedRoute>
                    }
                />

                {/* ================================= */}
                {/* LAYOUT EDITOR */}
                {/* ================================= */}

                <Route
                    path="/facility/:facilityId/layout-editor"
                    element={
                        <ProtectedRoute>
                            <LayoutEditor />
                        </ProtectedRoute>
                    }
                />

                {/* ================================= */}
                {/* DEFAULT ROUTE */}
                {/* ================================= */}

                <Route
                    path="/"
                    element={
                        <Navigate
                            to="/dashboard"
                            replace
                        />
                    }
                />

                {/* ================================= */}
                {/* FALLBACK ROUTE */}
                {/* ================================= */}

                <Route
                    path="*"
                    element={
                        <Navigate
                            to="/dashboard"
                            replace
                        />
                    }
                />

            </Routes>

        </BrowserRouter>
    );
}

export default App;